import { create } from 'zustand';

export interface Session {
  accessToken: string;
  refreshToken: string;
  user: { id: string; username: string; fullName: string; tenantId: string };
  roles: string[];
  permissions: string[];
  branchIds: string[];
}

interface AuthState {
  accessToken: string | null;
  session: Session | null;
  signIn: (session: Session) => void;
  signOut: () => void;
  can: (permission: string) => boolean;
  /** Set while a renewal is in flight, so ten parallel 401s cause one refresh. */
  renewal: Promise<boolean> | null;
  setRenewal: (p: Promise<boolean> | null) => void;
}

const KEY = 'aurum.session';

/**
 * The session survives a refresh, and ends with the tab.
 *
 * It used to live in memory alone, so pressing F5 — or opening a printed bill
 * in a new tab — signed the person out mid-job. That is not security, it is an
 * interruption: the till is still logged in on the next tab and the session is
 * still valid on the server.
 *
 * So it is kept in sessionStorage: a reload keeps you where you were, closing
 * the browser ends it, and another tab of another shop cannot see it. What
 * actually protects an unattended counter is the thirty-minute idle lock, which
 * is unchanged, and the token's own expiry.
 */
function load(): Session | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    // Anything short of a complete session is treated as none at all.
    return parsed?.accessToken && parsed?.refreshToken && parsed?.user?.id ? parsed : null;
  } catch {
    return null;
  }
}

function save(session: Session | null): void {
  try {
    if (session) sessionStorage.setItem(KEY, JSON.stringify(session));
    else sessionStorage.removeItem(KEY);
  } catch {
    // A browser with storage switched off still works; it just signs out on
    // refresh, exactly as before.
  }
}

const restored = load();

export const useAuth = create<AuthState>((set, get) => ({
  accessToken: restored?.accessToken ?? null,
  session: restored,
  signIn: (session) => {
    save(session);
    set({ accessToken: session.accessToken, session });
  },
  signOut: () => {
    save(null);
    set({ accessToken: null, session: null });
  },
  can: (permission) => get().session?.permissions.includes(permission) ?? false,
  renewal: null,
  setRenewal: (renewal) => set({ renewal }),
}));

/** Keep the stored copy in step when a token is renewed mid-shift. */
useAuth.subscribe((state) => {
  if (state.session && state.accessToken) {
    save({ ...state.session, accessToken: state.accessToken });
  }
});
